import { z } from 'zod';
import { CLINICAL_ORDER_TYPE, ENCOUNTER_CONTEXT } from '../enums';
import { CLAIM_FLAG, COMPLAINT_DURATION_UNIT, CONDITION_ONSET, DISPOSITION, rosFindingsSchema } from './emr-core';
import { EMR_NOT_NEEDED_KEYS, VITALS_NOT_TAKEN_REASON_MAX, VITALS_NOT_TAKEN_REASON_MIN } from '../emr-completion';
import { ORDER_FULFILMENT, orderLineQuantitySchema } from '../order-lines';
export const vitalsSchema = z.object({
    bpSystolic: z.number().int().min(50).max(260).nullable().optional(),
    bpDiastolic: z.number().int().min(25).max(160).nullable().optional(),
    pulse: z.number().int().min(25).max(250).nullable().optional(),
    respiratoryRate: z.number().int().min(4).max(80).nullable().optional(),
    temperatureC: z.number().min(30).max(44.5).nullable().optional(),
    spo2: z.number().int().min(40).max(100).nullable().optional(),
    heightCm: z.number().min(30).max(250).nullable().optional(),
    weightKg: z.number().min(0.3).max(350).nullable().optional(),
    painScore: z.number().int().min(0).max(10).nullable().optional(),
    bloodSugarMgDl: z.number().int().min(10).max(1200).nullable().optional(),
    headCircumferenceCm: z.number().min(20).max(70).nullable().optional(),
    notes: z.string().max(1000).optional(),
});
export function vitalsProblems(vitals) {
    const problems = [];
    const systolic = vitals.bpSystolic ?? null;
    const diastolic = vitals.bpDiastolic ?? null;
    if ((systolic === null) !== (diastolic === null)) {
        problems.push({
            path: systolic === null ? 'bpSystolic' : 'bpDiastolic',
            message: 'Record the systolic and the diastolic pressure together',
        });
    }
    else if (systolic !== null && diastolic !== null && systolic <= diastolic) {
        problems.push({ path: 'bpSystolic', message: 'The systolic pressure must be higher than the diastolic' });
    }
    const height = vitals.heightCm ?? null;
    const weight = vitals.weightKg ?? null;
    if (height !== null && weight !== null && height > 0) {
        const bmi = weight / (height / 100) ** 2;
        if (bmi < 8 || bmi > 90) {
            problems.push({ path: 'weightKg', message: `Height and weight do not fit together (BMI ${Math.round(bmi)})` });
        }
    }
    return problems;
}
export const VITAL_MEASUREMENT_FIELDS = [
    'bpSystolic',
    'bpDiastolic',
    'pulse',
    'respiratoryRate',
    'temperatureC',
    'spo2',
    'heightCm',
    'weightKg',
    'painScore',
    'bloodSugarMgDl',
    'headCircumferenceCm',
];
export const VITALS_RECHECK_REASON_MIN = 3;
export const VITALS_RECHECK_REASON_MAX = 300;
export function hasVitalMeasurement(vitals) {
    return VITAL_MEASUREMENT_FIELDS.some((field) => vitals[field] !== null && vitals[field] !== undefined);
}
export const recordVitalsSchema = vitalsSchema
    .extend({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    appointmentId: z.string().uuid().nullable().optional(),
    recordedAt: z.string().datetime().optional(),
    recheckReason: z.string().trim().max(VITALS_RECHECK_REASON_MAX).nullable().optional(),
})
    .superRefine((value, context) => {
    for (const problem of vitalsProblems(value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [problem.path], message: problem.message });
    }
    if (!hasVitalMeasurement(value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['pulse'], message: 'Record at least one measurement' });
    }
});
/** Free-form but templated clinical narrative; templates vary by specialty (SRS 6.3). */
export const encounterNotesSchema = z.object({
    presentingComplaints: z.string().max(4000).optional(),
    historyOfPresentIllness: z.string().max(8000).optional(),
    pastHistory: z.string().max(4000).optional(),
    surgicalHistory: z.string().max(4000).optional(),
    familyHistory: z.string().max(2000).optional(),
    socialHistory: z.string().max(2000).optional(),
    drugHistory: z.string().max(2000).optional(),
    examination: z.string().max(8000).optional(),
    reviewOfSystems: z.string().max(4000).optional(),
    rosFindings: rosFindingsSchema.optional(),
    investigationsAdvised: z.string().max(4000).optional(),
    treatmentPlan: z.string().max(8000).optional(),
    advice: z.string().max(4000).optional(),
    /** Visible only to the authoring doctor (SRS 6.3 private vs shareable notes). */
    privateNotes: z.string().max(4000).optional(),
    /** Specialty-specific structured payload, e.g. dental charting or obstetric LMP/EDD. */
    specialty: z.record(z.string(), z.unknown()).optional(),
});
export const openEncounterSchema = z.object({
    patientId: z.string().uuid(),
    appointmentId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    doctorId: z.string().uuid().optional(),
    branchId: z.string().uuid().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    context: z.enum(ENCOUNTER_CONTEXT).default('opd'),
    chiefComplaint: z.string().trim().max(400).optional(),
    feeOverrideReason: z.string().trim().min(5).max(300).optional(),
    backdateReason: z.string().trim().min(5).max(300).optional(),
});
export const updateEncounterSchema = z.object({
    chiefComplaint: z.string().trim().max(400).nullable().optional(),
    complaintDurationValue: z.number().int().min(1).max(999).nullable().optional(),
    complaintDurationUnit: z.enum(COMPLAINT_DURATION_UNIT).nullable().optional(),
    claimFlags: z.array(z.enum(CLAIM_FLAG)).max(CLAIM_FLAG.length).optional(),
    disposition: z.enum(DISPOSITION).nullable().optional(),
    noKnownAllergies: z.literal(true).optional(),
    allergiesVerified: z.literal(true).optional(),
    notNeeded: z.array(z.enum(EMR_NOT_NEEDED_KEYS)).max(EMR_NOT_NEEDED_KEYS.length).optional(),
    vitalsNotTakenReason: z.string().trim().min(VITALS_NOT_TAKEN_REASON_MIN).max(VITALS_NOT_TAKEN_REASON_MAX).nullable().optional(),
    notes: encounterNotesSchema.optional(),
    allergies: z
        .array(z.object({
        substance: z.string().trim().min(1).max(160),
        reaction: z.string().trim().max(240).optional(),
        severity: z.enum(['mild', 'moderate', 'severe']).default('moderate'),
    }))
        .optional(),
    followUpDate: z.string().datetime().nullable().optional(),
});
export const diagnosisSchema = z.object({
    icdCode: z.string().trim().min(1).max(20),
    icdTitle: z.string().trim().min(1).max(400),
    isPrimary: z.boolean().default(false),
    certainty: z.enum(['provisional', 'confirmed', 'differential', 'ruled_out']).default('provisional'),
    conditionOnset: z.enum(CONDITION_ONSET).optional(),
    addToProblemList: z.boolean().optional(),
    notes: z.string().max(1000).optional(),
});
export const setDiagnosesSchema = z.object({
    diagnoses: z.array(diagnosisSchema).max(30),
});
export const PRESCRIPTION_ROUTE = [
    'oral',
    'sublingual',
    'buccal',
    'iv',
    'im',
    'sc',
    'intradermal',
    'topical',
    'transdermal',
    'inhalation',
    'rectal',
    'vaginal',
    'ophthalmic',
    'otic',
    'nasal',
    'other',
];
export const prescriptionRouteSchema = z.enum(PRESCRIPTION_ROUTE, { errorMap: () => ({ message: 'Choose the route' }) });
const FORM_ROUTES = [
    [/eye|ophthalm/, 'ophthalmic'],
    [/\bear\b|otic/, 'otic'],
    [/nasal|nose/, 'nasal'],
    [/inhal|nebul|rotacap|puff|\bmdi\b/, 'inhalation'],
    [/suppositor|rectal|enema/, 'rectal'],
    [/pessar|vaginal/, 'vaginal'],
    [/patch|transdermal/, 'transdermal'],
    [/sublingual/, 'sublingual'],
    [/buccal|lozenge/, 'buccal'],
    [/cream|ointment|\bgel\b|lotion|topical/, 'topical'],
    [/tablet|\btab\b|capsule|\bcap\b|syrup|suspension|sachet|elixir/, 'oral'],
];
export function routeForForm(form) {
    const text = (form ?? '').trim().toLowerCase();
    if (!text)
        return null;
    const hit = FORM_ROUTES.find(([pattern]) => pattern.test(text));
    return hit ? hit[1] : null;
}
export const prescriptionItemSchema = z.object({
    productId: z.string().uuid().nullable().optional(),
    /** Free-text is allowed for drugs outside the tenant formulary. */
    drugName: z.string().trim().min(1).max(240),
    strength: z.string().trim().max(80).optional(),
    form: z.string().trim().max(60).optional(),
    dose: z.string().trim().min(1).max(80),
    route: prescriptionRouteSchema,
    frequency: z.string().trim().min(1).max(60),
    durationDays: z.number().int().min(0).max(365).default(0),
    quantity: z.number().min(0).max(10000).default(0),
    instructions: z.string().trim().max(400).optional(),
    isSos: z.boolean().default(false),
});
export const createPrescriptionSchema = z.object({
    encounterId: z.string().uuid().nullable().optional(),
    patientId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    items: z.array(prescriptionItemSchema).min(1).max(50),
    notes: z.string().max(2000).optional(),
    /** Set when the tenant has no Pharmacy module: the Rx prints as "external". */
    isExternal: z.boolean().default(false),
    /** Acknowledged interaction/allergy warnings, stored for audit. */
    acknowledgedWarnings: z.array(z.string().max(300)).default([]),
});
export const rxSetItemSchema = prescriptionItemSchema.extend({
    route: prescriptionRouteSchema.optional(),
});
export const rxSetSchema = z.object({
    name: z.string().trim().min(2).max(120),
    items: z.array(rxSetItemSchema).min(1).max(50),
    isShared: z.boolean().default(false),
});
export const createClinicalOrderSchema = z.object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    type: z.enum(CLINICAL_ORDER_TYPE),
    priority: z.enum(['routine', 'urgent', 'stat']).default('routine'),
    /** Lab test ids, radiology study ids, charge item ids or a free-text referral target. */
    items: z
        .array(z.object({
        refId: z.string().uuid().nullable().optional(),
        chargeItemId: z.string().uuid().optional(),
        label: z.string().trim().min(1).max(240),
        notes: z.string().max(600).optional(),
        quantity: orderLineQuantitySchema.default(1),
        fulfilment: z.enum(ORDER_FULFILMENT).optional(),
    }))
        .min(1)
        .max(50),
    clinicalNotes: z.string().max(2000).optional(),
    pregnancyChecked: z.boolean().optional(),
    pregnant: z.boolean().optional(),
    pregnancyJustification: z.string().trim().max(600).optional(),
    erCaseId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().optional(),
    collectAt: z.string().datetime().nullable().optional(),
    outside: z.boolean().default(false),
}).superRefine((value, context) => {
    if (value.pregnant && (value.pregnancyJustification ?? '').length < 5) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['pregnancyJustification'], message: 'Say why the imaging goes ahead in pregnancy' });
    }
    if (value.type !== 'lab' && value.type !== 'radiology') {
        if (value.outside)
            context.addIssue({ code: z.ZodIssueCode.custom, path: ['outside'], message: 'Only tests and imaging can be written as an outside requisition' });
        return;
    }
    const services = value.items.map((item) => item.chargeItemId).filter((id) => Boolean(id));
    if (new Set(services).size !== services.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['items'], message: 'The same service is listed twice; raise its quantity instead' });
    }
    value.items.forEach((item, index) => {
        if (value.outside && (item.refId || item.chargeItemId)) {
            context.addIssue({ code: z.ZodIssueCode.custom, path: ['items', index, 'refId'], message: 'An outside requisition is free text, not a catalogue item' });
        }
        if (value.outside || item.refId || item.chargeItemId)
            return;
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['items', index, 'refId'],
            message: value.type === 'lab' ? 'Pick the test from the lab catalogue' : 'Pick the study from the imaging catalogue',
        });
    });
});
export const investigationListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().max(80).optional(),
});
export const movedRequestDecisionSchema = z.object({
    decision: z.enum(['reordered', 'dismissed']),
});
export const issueCertificateSchema = z
    .object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    type: z.enum(['sick_leave', 'fitness', 'medical', 'attendance']),
    fromDate: z.string().datetime().nullable().optional(),
    toDate: z.string().datetime().nullable().optional(),
    days: z.number().int().min(1).max(365).optional(),
    externalRef: z.string().trim().min(3).max(60).optional(),
    body: z.string().trim().min(10).max(4000),
})
    .refine((value) => !value.fromDate || !value.toDate || value.toDate >= value.fromDate, {
    path: ['toDate'],
    message: 'The period must end on or after it starts',
});
export const SICK_LEAVE_AUTHORITY = { SA: 'seha' };
export function sickLeaveAuthorityFor(country) {
    return country ? (SICK_LEAVE_AUTHORITY[country.toUpperCase()] ?? null) : null;
}
export function certificateDays(fromDate, toDate) {
    if (!fromDate || !toDate)
        return null;
    const from = Date.parse(fromDate.slice(0, 10));
    const to = Date.parse(toDate.slice(0, 10));
    if (Number.isNaN(from) || Number.isNaN(to) || to < from)
        return null;
    return Math.round((to - from) / 86_400_000) + 1;
}
//# sourceMappingURL=clinical.js.map